import { useLanguage } from "../contexts/LanguageContext";
import React from "react";
import { Card, CardContent } from "../Components/ui/card";
import { Badge } from "../Components/ui/badge";
import { 
  Clock,
  Bell,
  Zap,
  Shield,
  Users,
  Award,
  CheckCircle,
  BarChart3,
  TrendingUp,
  Database,
  Settings,
  DollarSign,
  Target,
} from "lucide-react";



const technicalArchitecture = [
  {
    icon: Database,
    title: "Cloud-native",
    description: "Elastic by design for reliability, performance, and global access."
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "Data encryption, SSO/SAML, role policies, and compliance-ready controls."
  },
  {
    icon: Zap,
    title: "Real-time Processing",
    description: "Instant updates across channels with sub-second sync."
  },
  {
    icon: BarChart3,
    title: "AI-powered Analytics",
    description: "Demand sensing, anomaly detection, and automated decision support."
  }
];

const businessBenefits = [
  {
    icon: DollarSign,
    title: "Reduce Costs",
    description: "Optimize inventory levels and minimize carrying costs with intelligent forecasting.",
    impact: "Up to 30% cost reduction"
  },
  {
    icon: TrendingUp,
    title: "Increase Sales",
    description: "Never miss a sale due to stockouts with predictive inventory management.",
    impact: "15% sales increase"
  },
  {
    icon: Target,
    title: "Improve Efficiency",
    description: "Automate routine tasks and streamline operations across your entire supply chain.",
    impact: "50% time savings"
  }
];

export default function SkuDock() {
  const { tx } = useLanguage();
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="skudock-hero" className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600">{tx("Coming Soon")}</Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8">
              <span className="text-gradient">{tx("SkuDock")}</span><br />{tx("The Best #1 e-Commerce SaaS Suite")}</h1>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto mb-10">{tx("The next-generation smart inventory management system that revolutionizes how businesses track, manage, and optimize their stock levels with cutting-edge technology and intelligent automation.")}</p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center text-emerald-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-base font-medium">{tx("Real-time Tracking")}</span>
              </div>
              <div className="flex items-center text-emerald-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-base font-medium">{tx("AI-Powered Analytics")}</span>
              </div>
              <div className="flex items-center text-emerald-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-base font-medium">{tx("Automated Alerts")}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Clock className="w-10 h-10 text-emerald-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{tx("Coming Soon")}</h2>
              <p className="text-gray-600 mb-6">{tx("SkuDock is in active development. Join the early access list to be the first to try our intelligent inventory system.")}</p>
              
              <div className="space-y-4">
                <div className="flex items-center text-gray-600">
                  <Bell className="w-5 h-5 mr-3 text-emerald-600" />
                  <span>{tx("Get notified when SkuDock launches")}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Zap className="w-5 h-5 mr-3 text-emerald-600" />
                  <span>{tx("Early access for beta testing")}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Award className="w-5 h-5 mr-3 text-emerald-600" />
                  <span>{tx("Exclusive launch pricing")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Preview Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">{tx("What to Expect from ")}<span className="text-gradient">{tx("SkuDock")}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{tx("A glimpse into the powerful features that will transform your inventory management.")}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover-lift text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{tx("Real-time Tracking")}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{tx("Monitor inventory levels and item movements across locations—without manual refresh.")}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <BarChart3 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{tx("Smart Analytics")}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{tx("AI forecasts demand and recommends reorder points to balance service level and cost.")}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Bell className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{tx("Automated Alerts")}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{tx("Get notified for low stock, anomalies, and late POs—before they impact sales.")}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{tx("Multi-user Access")}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{tx("Role-based access and audit trails for teams, warehouses, and partners.")}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Settings className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{tx("Integration-ready")}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{tx("Connect to your WMS/ERP/ecommerce stack with ready-to-use connectors and APIs.")}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{tx("Scalable Solution")}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{tx("Start with one site and scale to enterprise—no change to your workflows.")}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>



      {/* Technical Architecture */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">{tx("Enterprise-Grade ")}<span className="text-gradient">{tx("Technology")}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{tx("Built with modern architecture and enterprise security standards to handle the most demanding inventory management requirements.")}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technicalArchitecture.map((tech, index) => (
              <Card key={index} className="border-0 shadow-lg hover-lift text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <tech.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {tx(tech.title)}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {tx(tech.description)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">{tx("Measurable ")}<span className="text-gradient">{tx("Business Impact")}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{tx("See real results with SkuDock's intelligent inventory management system. Our customers report significant improvements across key business metrics.")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {businessBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover-lift text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {tx(benefit.title)}
                  </h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {tx(benefit.description)}
                  </p>
                  <div className="inline-block bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold">
                    {tx(benefit.impact)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">{tx("Why Choose ")}<span className="text-gradient">{tx("SkuDock")}</span>?
              </h2>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">{tx("Built with the same innovative spirit as Kylendar, SkuDock represents the future of inventory management technology.")}</p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{tx("Smarter Replenishment")}</h3>
                    <p className="text-lg text-gray-600">{tx("AI-driven reorder points tuned to your lead times and service goals.")}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{tx("End-to-End Visibility")}</h3>
                    <p className="text-lg text-gray-600">{tx("One place to see inventory, orders, and exceptions across sites.")}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{tx("Built to Integrate")}</h3>
                    <p className="text-lg text-gray-600">{tx("Works with your existing systems and ecommerce channels from day one.")}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-100 to-green-100 rounded-3xl p-8">
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">{tx("Coming Soon")}</h3>
                  <p className="text-lg text-gray-600 mb-8">{tx("We're preparing the next step in intelligent inventory management.")}</p>
                  <div className="text-5xl font-bold text-emerald-600 mb-3">Q4 2025</div>
                  <p className="text-base text-gray-500">{tx("Join the waitlist for beta access and launch-day pricing.")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-green">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{tx("Be the First to Experience SkuDock")}</h2>
          <p className="text-2xl text-emerald-100 mb-10 max-w-3xl mx-auto">{tx("Join the early access program to be among the first to try SkuDock and help shape the launch roadmap.")}</p>
        </div>
      </section>
    </div>
  );
}
